import { Card } from "@/components/ui/card";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import MetricCard from "./MetricCard";
import { DollarSign, Users, TrendingUp, Percent, PieChart as PieChartIcon, BarChart3 } from "lucide-react";

const COLORS = {
  holding_company: "#6b7280",
  angel_investor: "#a8b545",
  private_equity: "#10b981",
  family_office: "#f59e0b",
  personal_investor: "#aecb3d",
};

const TYPE_LABELS = {
  holding_company: "Holding selskap",
  angel_investor: "Engle investor",
  private_equity: "Private Equity",
  family_office: "Family office",
  personal_investor: "Personlig investor",
};

export default function PortfolioAnalysis({ investors, investments = [] }) {
  // Calculate metrics from investments
  const totalInvestment = investments.reduce((sum, i) => sum + (i.investment_amount || 0), 0);
  const activeInvestments = investments.filter(i => i.status === "active");
  const exitedInvestments = investments.filter(i => i.status === "exited");
  const avgInvestment = investments.length > 0 ? totalInvestment / investments.length : 0;
  const totalOwnership = investments.reduce((sum, i) => sum + (i.ownership_percentage || 0), 0);
  const investedCount = investors.filter(i => i.status === "invested").length;

  // Investment by type (aggregate investments by investor type)
  const investmentByType = investments.reduce((acc, investment) => {
    const investor = investors.find(inv => inv.id === investment.investor_id);
    const type = investor?.type || "other";
    if (!acc[type]) {
      acc[type] = { type, amount: 0, count: 0 };
    }
    acc[type].amount += investment.investment_amount || 0;
    acc[type].count += 1;
    return acc;
  }, {});

  const typeChartData = Object.values(investmentByType).map(item => ({
    name: TYPE_LABELS[item.type] || item.type,
    value: item.amount,
    count: item.count,
  }));

  // Investment by status
  const investmentByStatus = investments.reduce((acc, investment) => {
    const status = investment.status || "active";
    if (!acc[status]) {
      acc[status] = 0;
    }
    acc[status] += investment.investment_amount || 0;
    return acc;
  }, {});

  const statusChartData = Object.entries(investmentByStatus).map(([status, amount]) => ({
    name: status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
    amount: amount,
  }));

  // Calculate ROI for exited investments
  const totalExitValue = exitedInvestments.reduce((sum, i) => sum + (i.exit_amount || 0), 0);
  const totalExitInvestment = exitedInvestments.reduce((sum, i) => sum + (i.investment_amount || 0), 0);
  const avgROI = totalExitInvestment > 0 
    ? ((totalExitValue - totalExitInvestment) / totalExitInvestment * 100)
    : 0;

  // Status distribution
  const statusDistribution = investors.reduce((acc, investor) => {
    const status = investor.status || "prospect";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
          <p className="font-medium text-slate-900">{payload[0].name}</p>
          <p className="text-sm text-slate-600">
            {payload[0].value?.toLocaleString()} kr
          </p>
          {payload[0].payload.count && (
            <p className="text-xs text-slate-500 mt-1">
              {payload[0].payload.count} investor{payload[0].payload.count !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <MetricCard
          title="Total Investment"
          value={`${totalInvestment.toLocaleString()} kr`}
          subtitle={`From ${investors.length} investors`}
          icon={DollarSign}
        />
        <MetricCard
          title="Active Investors"
          value={investedCount}
          subtitle={`${statusDistribution.invested || 0} currently invested`}
          icon={Users}
        />
        <MetricCard
          title="Avg Investment"
          value={`${avgInvestment.toLocaleString(undefined, { maximumFractionDigits: 0 })} kr`}
          subtitle="Per investor"
          icon={TrendingUp}
        />
        <MetricCard
        title="Portfolio Size"
        value={investments.length}
        subtitle={`${activeInvestments.length} active, ${exitedInvestments.length} exited`}
        icon={Percent}
        trend={avgROI !== 0 ? `${avgROI.toFixed(0)}%` : null}
        trendUp={avgROI > 0}
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Investment by Type */}
        <Card className="p-6 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-lime-100">
              <PieChartIcon className="w-5 h-5 text-zinc-900" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Investment by Type</h3>
              <p className="text-sm text-slate-500">Distribution across investor categories</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={typeChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                innerRadius={62}
                outerRadius={100}
                paddingAngle={3}
                cornerRadius={5}
                stroke="none"
                fill="#141414"
                dataKey="value"
              >
                {typeChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[Object.keys(investmentByType)[index]] || "#94a3b8"} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        {/* Investment by Status */}
        <Card className="p-6 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-emerald-100">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Investment by Stage</h3>
              <p className="text-sm text-slate-500">Capital allocation by status</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={statusChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12 }} 
                stroke="#94a3b8"
              />
              <YAxis 
                tick={{ fontSize: 12 }} 
                stroke="#94a3b8"
                tickFormatter={(value) => `${(value / 1000).toFixed(0)}k kr`}
              />
              <Tooltip 
                formatter={(value) => `${value.toLocaleString()} kr`}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="amount" fill="#10b981" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Top Investments */}
        <Card className="p-6 bg-white border-0 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 mb-6">Top Investments</h3>
          <div className="space-y-4">
            {investments
              .sort((a, b) => (b.investment_amount || 0) - (a.investment_amount || 0))
              .slice(0, 5)
              .map((investment, index) => {
                const investor = investors.find(inv => inv.id === investment.investor_id);
                return (
                  <div key={investment.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm`}
                           style={{ backgroundColor: COLORS[investor?.type] || "#94a3b8" }}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{investment.company_name}</p>
                        <p className="text-xs text-slate-500">{investor?.name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">
                        {investment.investment_amount.toLocaleString()} kr
                      </p>
                      {investment.ownership_percentage > 0 && (
                        <p className="text-xs text-slate-500">{investment.ownership_percentage}% equity</p>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </Card>

        {/* Investment Status Summary */}
        <Card className="p-6 bg-white border-0 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 mb-6">Status Overview</h3>
          <div className="space-y-4">
            {Object.entries(statusDistribution)
              .sort((a, b) => b[1] - a[1])
              .map(([status, count]) => {
                const percentage = (count / investors.length) * 100;
                const statusColors = {
                  prospect: "bg-slate-500",
                  in_discussion: "bg-blue-500",
                  committed: "bg-amber-500",
                  invested: "bg-emerald-500",
                  exited: "bg-rose-500",
                };
                return (
                  <div key={status}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700 capitalize">
                        {status.replace(/_/g, " ")}
                      </span>
                      <span className="text-sm text-slate-500">
                        {count} ({percentage.toFixed(0)}%)
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${statusColors[status] || "bg-slate-400"}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
          
          {exitedInvestments.length > 0 && (
            <div className="mt-6 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-900">ROI Performance</span>
              </div>
              <p className="text-2xl font-bold text-emerald-700">{avgROI.toFixed(1)}%</p>
              <p className="text-xs text-emerald-600 mt-1">
                Average return on {exitedInvestments.length} exited investment{exitedInvestments.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}